import React from "react";
import { motion } from "framer-motion";
import { Cpu, HardDrive, MemoryStick } from "lucide-react";

const CPUS = [
  { name: "Ryzen 9 3900X", cores: "12C/24T", boost: "4.6 GHz", ddr: "DDR4", power: 60 },
  { name: "Ryzen 9 5900X", cores: "12C/24T", boost: "4.8 GHz", ddr: "DDR4", power: 70 },
  { name: "Ryzen 9 5950X", cores: "16C/32T", boost: "4.9 GHz", ddr: "DDR4", power: 80 },
  { name: "Ryzen 9 7950X", cores: "16C/32T", boost: "5.7 GHz", ddr: "DDR5", power: 92 },
  { name: "Ryzen 9 9900X", cores: "12C/24T", boost: "5.6 GHz", ddr: "DDR5", power: 100 },
];

export const HardwareShowcase = () => {
  return (
    <section className="relative py-20 sm:py-24 overflow-hidden" data-testid="hardware-showcase">
      <div className="absolute inset-0 osx-grid-bg opacity-30" aria-hidden />
      <div className="osx-container relative">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="text-xs font-mono uppercase tracking-[0.25em] mb-3" style={{ color: "var(--osx-brand-600)" }}>
              Bare Metal
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
              Hardware <span className="osx-gradient-text">sin compromisos</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
              Servidores dedicados con procesadores AMD Ryzen 9, memoria DDR4/DDR5 de alta frecuencia y almacenamiento NVMe. Toda la potencia, para vos solo.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { icon: Cpu, label: "CPU", value: "Ryzen 9" },
                { icon: MemoryStick, label: "RAM", value: "Hasta 192GB" },
                { icon: HardDrive, label: "Disco", value: "NVMe 4TB" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="p-3 rounded-xl"
                  style={{ background: "var(--osx-surface)", border: "1px solid var(--osx-border-strong)" }}
                >
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>
                    <s.icon size={11} /> {s.label}
                  </div>
                  <div className="mt-1 text-sm font-bold" style={{ color: "var(--osx-text)" }}>
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: CPU timeline */}
          <div className="lg:col-span-7 space-y-3">
            {CPUS.map((cpu, idx) => (
              <motion.div
                key={cpu.name}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                whileHover={{ x: -4 }}
                className="relative p-4 sm:p-5 rounded-2xl flex items-center gap-4"
                style={{
                  background: "var(--osx-surface)",
                  border: "1px solid var(--osx-border)",
                  boxShadow: "var(--osx-shadow-1)",
                  transition: "border-color 0.25s ease",
                }}
                data-testid={`cpu-row-${idx}`}
              >
                <div
                  className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-xl font-mono font-bold text-white"
                  style={{
                    background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                    boxShadow: "0 10px 25px rgba(2,132,199,0.32)",
                  }}
                >
                  R9
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="text-base sm:text-lg font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
                      {cpu.name}
                    </h3>
                    <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-md"
                      style={{ background: "var(--osx-surface-2)", color: "var(--osx-brand-600)", border: "1px solid var(--osx-border)" }}>
                      {cpu.ddr}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs font-mono" style={{ color: "var(--osx-text-muted)" }}>
                    <span>{cpu.cores}</span>
                    <span>·</span>
                    <span>Boost {cpu.boost}</span>
                  </div>
                  <div
                    className="mt-2.5 h-1.5 w-full rounded-full overflow-hidden"
                    style={{ background: "var(--osx-surface-2)" }}
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${cpu.power}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.9, delay: 0.2 + idx * 0.08, ease: "easeOut" }}
                      className="h-full rounded-full"
                      style={{
                        background:
                          "linear-gradient(90deg, var(--osx-brand-400), var(--osx-accent-cyan))",
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
